import { Certificate, DropdownVessel } from "../../../certificates/models/certificates.interfaces";
import { useDropdownVessels } from "../../../certificates/services/certificates.service";
import { featureFlagsAtom, themeAtom } from "../../../shared/atoms";
import Breadcrumb from "../../../shared/components/Breadcrumb";
import HeaderTitle from "../../../shared/components/HeaderTitle";
import DocumentInformation from "./DocumentInformation";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  documents: Certificate[];
  pristine: boolean;
  onVesselSelected: (fileName: string, vessel: DropdownVessel) => void;
}

const StepTwo: React.FC<Props> = ({ documents, pristine, onVesselSelected }) => {
  const theme = useAtomValue(themeAtom);

  const { vessels } = useDropdownVessels();
  const [idxOfFirstDocumentWithVesselSelected, setIdxOfFirstDocumentWithVesselSelected] = useState<number | null>(null);
  const featureFlags = useAtomValue(featureFlagsAtom);

  useEffect(() => {
    if (idxOfFirstDocumentWithVesselSelected !== null) {
      return;
    }
    const idx = documents.findIndex((doc) => doc.vesselName !== "");
    setIdxOfFirstDocumentWithVesselSelected(idx === -1 ? null : idx);
  }, [documents, idxOfFirstDocumentWithVesselSelected]);

  return (
    <>
      <div className="mb-8 flex flex-col gap-[33px]">
        <HeaderTitle title={"Upload a certificate or document"} />
        <Breadcrumb currentStep={2} totalSteps={2} />
      </div>

      {featureFlags.opslens_enabled && (
        <p className={twMerge("mb-6", theme.uwhcAssistant.text)}>
          Please review below fields (extracted using AI) & complete all the missing information for each document.
        </p>
      )}

      {!featureFlags.opslens_enabled && (
        <p className={twMerge("mb-6", theme.uwhcAssistant.text)}>
          Complete all the required information for each document.
        </p>
      )}

      {featureFlags.opslens_enabled && (
        <p className={twMerge("text-xs leading-5", theme.myVesselsTableRow.tableText.primary)}>
          ⚠️ Disclaimer: AI outputs may contain errors and should not be treated as source of truth. You are responsible
          for verification and compliance with internal policies
        </p>
      )}

      <div data-testid="document-list" className="flex flex-1 flex-col overflow-y-auto">
        {documents.map((doc, idx) => (
          <DocumentInformation
            document={doc}
            key={doc.fileName}
            vesselOptions={vessels}
            showVesselSyncButton={documents.length >= 2 && idxOfFirstDocumentWithVesselSelected === idx}
            showErrors={!pristine}
            onVesselSelected={onVesselSelected}
          />
        ))}
      </div>
    </>
  );
};

export default StepTwo;
